/**
 * How a field photo reads as a report card.
 *
 * The phone sends kind, notes, a fix, and (sometimes) accuracy. DataHog
 * adds caption, OCR, objects, and faces. This file splits those into
 * operator-reported vs system-assessed so the card does not mix them.
 */
import { MissionFieldReport, MissionTimelineStop } from '../../services/missionApi';
import { formatDtg, formatMgrs } from './threatReportView';
import { formatMeters } from './format';
import { photoTitle } from './photoView';

export type FieldPriority = 'ROUTINE' | 'PRIORITY';

export interface FieldReportCardModel {
  id: string;
  reportId: string;
  title: string;
  priority: FieldPriority;
  dtg: string;
  observed: {
    category: string;
    identifier?: string;
    duration: string;
    note?: string;
    attribution?: string;
  };
  assessed: {
    alias?: string;
    aka?: string;
    plate?: string;
    plateNote?: string;
    scene?: string;
    prior?: string;
    matchBasis?: string;
    warning?: string;
    identified?: string[];
    watchlisted: boolean;
  };
  photoIndex: number;
  photoTotal: number;
  geometry: {
    observerMgrs?: string;
    subjectMgrs?: string;
    decimal: string;
    accuracy?: string;
    route?: string;
    latitude: number;
    longitude: number;
  };
  source: { grade: string; label: string };
  confidence: { label: string; detail: string };
  filing?: string;
  relatedId?: string;
  relatedReportId?: string;
}

const PLATE = /\b([A-Z]{1,3}\s?\d{1,4}[A-Z0-9]{0,3}|\d{1,3}\s?[A-Z]{2,4}\s?\d{1,4})\b/;

function slug(text: string): string {
  const first = text.trim().split(/\s+/)[0] || 'MSN';
  return first.toUpperCase().replace(/[^A-Z0-9]+/g, '').slice(0, 8) || 'MSN';
}

function namedFaces(report: MissionFieldReport): string[] {
  return (report.face_matches || [])
    .map((name) => name.split(' (cluster')[0].trim())
    .filter(Boolean);
}

function plateFromOcr(ocr?: string | null): { plate: string; note?: string } | null {
  const text = (ocr || '').toUpperCase().replace(/[•·]/g, ' ');
  const match = text.match(PLATE);
  if (!match) return null;
  const plate = match[1].replace(/\s+/g, ' ').trim();
  const low = /[?]/.test(ocr || '') || plate.length < 5;
  return { plate, note: low ? 'partial, low confidence' : undefined };
}

function aliasOf(report: MissionFieldReport): string | undefined {
  const named = namedFaces(report)[0];
  if (named) return named.toUpperCase().replace(/\s+/g, '-');
  if (report.identifier) return report.identifier.toUpperCase().replace(/\s+/g, '-').slice(0, 18);
  const caption = (report.caption || '').trim();
  if (caption) return caption.toUpperCase().replace(/[^A-Z0-9]+/g, '-').slice(0, 18);
  return undefined;
}

function sceneOf(report: MissionFieldReport): string | undefined {
  const objects = (report.objects || []).filter(Boolean);
  if (objects.length) return objects.slice(0, 4).join(', ');
  const tags = (report.tags || []).filter(Boolean);
  if (tags.length) return tags.join(', ');
  return (report.caption || report.scene || '').trim() || undefined;
}

function relatedReport(
  report: MissionFieldReport,
  all: MissionFieldReport[],
): MissionFieldReport | undefined {
  const names = new Set(namedFaces(report).map((n) => n.toLowerCase()));
  const ident = (report.identifier || '').trim().toLowerCase();
  return all.find((other) => {
    if (other.id === report.id) return false;
    if (ident && (other.identifier || '').trim().toLowerCase() === ident) return true;
    return namedFaces(other).some((n) => names.has(n.toLowerCase()));
  });
}

function priorLine(report: MissionFieldReport, all: MissionFieldReport[]): string | undefined {
  const names = new Set(namedFaces(report).map((n) => n.toLowerCase()));
  const ident = (report.identifier || '').trim().toLowerCase();
  if (!names.size && !ident) return undefined;
  const matches = all.filter((other) => {
    if (other.id === report.id) return false;
    if (ident && (other.identifier || '').trim().toLowerCase() === ident) return true;
    return namedFaces(other).some((n) => names.has(n.toLowerCase()));
  });
  if (!matches.length) return undefined;
  return `${matches.length} other report${matches.length === 1 ? '' : 's'} this mission`;
}

function matchBasis(report: MissionFieldReport): string | undefined {
  const faces = (report.faces || []).filter((f) => f.label);
  const best = faces
    .map((f) => Number(f.confidence))
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => b - a)[0];
  if (best != null) {
    const score = best > 1 ? best / 100 : best;
    return `Visual similarity ${score.toFixed(2)}${report.identifier ? '' : ' · no plate confirm'}`;
  }
  if (namedFaces(report).length) return 'Named face match';
  if (report.ocr_text) return 'OCR only';
  return undefined;
}

function warningOf(report: MissionFieldReport): string | undefined {
  if (report.face_status && /queue|pending|wait/i.test(report.face_status)) {
    return `Enrichment partial — occupant identification ${report.face_status}`;
  }
  if (report.watchlisted) return undefined;
  if (namedFaces(report).length === 0 && (report.caption || report.ocr_text)) {
    return 'Enrichment partial — no confirmed identity';
  }
  return undefined;
}

function routeContext(
  report: MissionFieldReport,
  timeline?: MissionTimelineStop[] | null,
  units: 'km' | 'mi' = 'mi',
): string | undefined {
  if (!timeline || timeline.length < 2) return undefined;
  let nearest = timeline[0];
  let best = Number.POSITIVE_INFINITY;
  for (const stop of timeline) {
    if (stop.latitude == null || stop.longitude == null) continue;
    const dlat = (stop.latitude - report.latitude) * 111_320;
    const dlng = (stop.longitude - report.longitude) * 111_320 * Math.cos((report.latitude * Math.PI) / 180);
    const dist = Math.hypot(dlat, dlng);
    if (dist < best) {
      best = dist;
      nearest = stop;
    }
  }
  const idx = Math.max(0, timeline.indexOf(nearest));
  const leg = Math.max(1, idx);
  const span = formatMeters(best, units);
  const name = nearest.label || `Waypoint ${idx + 1}`;
  return span ? `Leg ${leg} · ${span} from ${name}` : `Leg ${leg} · ${name}`;
}

function sourceOf(report: MissionFieldReport): FieldReportCardModel['source'] {
  if (report.device_owner) return { grade: 'B2', label: 'Trained observer / probably true' };
  return { grade: 'C3', label: 'Fairly reliable / device upload' };
}

function confidenceOf(report: MissionFieldReport): FieldReportCardModel['confidence'] {
  if (report.watchlisted) return { label: 'High', detail: 'Watchlisted identity on the photo' };
  if (namedFaces(report).length) return { label: 'Moderate', detail: 'Visual match, plate unconfirmed' };
  if (report.ocr_text || report.caption) return { label: 'Low', detail: 'Scene / OCR only' };
  return { label: 'Low', detail: 'Photo only, no enrichment' };
}

function captureOrder(all: MissionFieldReport[]): MissionFieldReport[] {
  return [...all].sort((a, b) => (
    new Date(a.observed_at || 0).getTime() - new Date(b.observed_at || 0).getTime()
  ));
}

export function fieldReportId(
  report: MissionFieldReport,
  all: MissionFieldReport[],
  missionName?: string,
): string {
  const seq = Math.max(1, captureOrder(all).findIndex((row) => row.id === report.id) + 1);
  return `FR-${slug(missionName || 'MSN')}-${String(seq).padStart(4, '0')}`;
}

export function buildFieldReportCard(
  report: MissionFieldReport,
  all: MissionFieldReport[],
  opts: { missionName?: string; timeline?: MissionTimelineStop[] | null; units?: 'km' | 'mi' } = {},
): FieldReportCardModel {
  const units = opts.units || 'mi';
  const ordered = captureOrder(all);
  const seq = Math.max(1, ordered.findIndex((row) => row.id === report.id) + 1);
  const related = relatedReport(report, all);
  const plate = plateFromOcr(report.ocr_text);
  const identified = namedFaces(report);
  const acc = report.accuracy_meters != null && report.accuracy_meters >= 0
    ? report.accuracy_meters
    : null;
  return {
    id: report.id,
    reportId: fieldReportId(report, all, opts.missionName),
    title: photoTitle(report),
    priority: report.watchlisted || identified.length > 0 ? 'PRIORITY' : 'ROUTINE',
    dtg: formatDtg(report.observed_at),
    observed: {
      category: (report.kind || 'Photo').trim() || 'Photo',
      identifier: (report.identifier || '').trim() || undefined,
      duration: 'Unknown at time of capture',
      note: (report.notes || '').trim() || undefined,
      attribution: report.device_owner
        ? `Operator note, verbatim — ${report.device_owner}`
        : undefined,
    },
    assessed: {
      alias: aliasOf(report),
      aka: related ? `${photoTitle(related)} — ${fieldReportId(related, all, opts.missionName)}` : undefined,
      plate: plate?.plate,
      plateNote: plate?.note,
      scene: sceneOf(report),
      prior: priorLine(report, all),
      matchBasis: matchBasis(report),
      warning: warningOf(report),
      identified,
      watchlisted: !!report.watchlisted,
    },
    photoIndex: seq,
    photoTotal: Math.max(1, ordered.length),
    geometry: {
      observerMgrs: formatMgrs(report.latitude, report.longitude),
      decimal: `${report.latitude.toFixed(4)}, ${report.longitude.toFixed(4)}`,
      accuracy: acc != null ? `±${Math.round(acc)} m` : undefined,
      route: routeContext(report, opts.timeline, units),
      latitude: report.latitude,
      longitude: report.longitude,
    },
    source: sourceOf(report),
    confidence: confidenceOf(report),
    filing: [
      report.device_owner || report.device_id,
      report.has_photo ? 'original retained' : null,
    ].filter(Boolean).join(' · ') || undefined,
    relatedId: related?.id,
    relatedReportId: related ? fieldReportId(related, all, opts.missionName) : undefined,
  };
}
